import { useState } from 'react';
import { RefreshCw, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { useHealthStatus } from '@/hooks/api/useHealth';
import { ServiceHealth } from '@/api/health';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

function ServiceCard({ service, lastChecked }: { service: ServiceHealth; lastChecked?: string }) {
  const isHealthy = service.status === 'healthy';
  const isUnhealthy = service.status === 'unhealthy';

  const formatServiceName = (name: string) => {
    return name
      .replace(/-/g, ' ')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div
      className={`bg-card rounded-lg border p-5 shadow-sm ${
        isUnhealthy ? 'border-red-200 dark:border-red-800' : isHealthy ? 'border-green-100 dark:border-green-800' : 'border-border'
      }`}
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-foreground">{formatServiceName(service.name)}</h3>
          {lastChecked && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Last checked: {new Date(lastChecked).toLocaleTimeString()}
            </p>
          )}
        </div>
        <Badge
          variant={isHealthy ? 'success' : isUnhealthy ? 'error' : 'default'}
          className="capitalize"
        >
          {service.status}
        </Badge>
      </div>

      <div className="mt-4 space-y-1">
        {service.responseTime !== undefined && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Response time</span>
            <span
              className={`font-medium ${
                service.responseTime < 200
                  ? 'text-green-600'
                  : service.responseTime < 1000
                    ? 'text-yellow-600'
                    : 'text-red-600'
              }`}
            >
              {service.responseTime}ms
            </span>
          </div>
        )}
        {isHealthy && (
          <div className="flex items-center gap-1.5 text-sm text-green-600 mt-2">
            <CheckCircle2 className="h-4 w-4" />
            <span>Operational</span>
          </div>
        )}
        {isUnhealthy && (
          <div className="flex items-center gap-1.5 text-sm text-red-600 mt-2">
            <XCircle className="h-4 w-4" />
            <span>Service unavailable</span>
          </div>
        )}
        {service.status === 'unknown' && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-2">
            <AlertCircle className="h-4 w-4" />
            <span>Status unknown</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SystemHealth() {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30);

  const { data: health, isLoading, isFetching, refetch, dataUpdatedAt } = useHealthStatus(
    autoRefresh ? refreshInterval * 1000 : false,
  );

  const isAllHealthy =
    health?.services?.every((s) => s.status === 'healthy') &&
    health?.status === 'healthy';
  const hasOutage =
    health?.services?.some((s) => s.status === 'unhealthy') ||
    (health !== undefined && health.status !== 'healthy');

  const getOverallStatus = () => {
    if (!health) return null;
    if (hasOutage) return { label: 'Outage Detected', variant: 'destructive' as const, color: 'bg-red-50 border-red-200 text-red-800' };
    if (isAllHealthy) return { label: 'All Systems Operational', variant: 'success' as const, color: 'bg-green-50 border-green-200 text-green-800' };
    return { label: 'Degraded Performance', variant: 'warning' as const, color: 'bg-yellow-50 border-yellow-200 text-yellow-800' };
  };

  const overallStatus = getOverallStatus();

  const formatTimestamp = (ts: string) => {
    return new Date(ts).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">System Health</h1>
          <p className="text-muted-foreground">Real-time status of all services</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3">
            <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} id="auto-refresh" />
            <label htmlFor="auto-refresh" className="text-sm text-muted-foreground cursor-pointer select-none">
              Auto-refresh
            </label>
            {autoRefresh && (
              <Select
                value={String(refreshInterval)}
                onValueChange={(v) => setRefreshInterval(Number(v))}
              >
                <SelectTrigger className="w-[80px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15s</SelectItem>
                  <SelectItem value="30">30s</SelectItem>
                  <SelectItem value="60">60s</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overall status banner */}
      {isLoading ? (
        <Skeleton className="h-16 w-full rounded-lg" />
      ) : overallStatus ? (
        <div className={`rounded-lg border p-4 ${overallStatus.color}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {hasOutage ? (
                <XCircle className="h-5 w-5 text-red-600" />
              ) : isAllHealthy ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-yellow-600" />
              )}
              <span className="font-semibold text-lg">{overallStatus.label}</span>
            </div>
            {health?.timestamp && (
              <span className="text-sm opacity-75">
                Last updated: {formatTimestamp(health.timestamp)}
              </span>
            )}
          </div>
        </div>
      ) : null}

      {/* Auto-refresh notice */}
      {dataUpdatedAt > 0 && (
        <p className="text-xs text-muted-foreground">
          {autoRefresh
            ? `Auto-refreshing every ${refreshInterval}s.`
            : 'Auto-refresh is off.'}{' '}
          Last refreshed at {new Date(dataUpdatedAt).toLocaleTimeString()}.
        </p>
      )}

      {/* Service cards grid */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-32 w-full rounded-lg" />
          ))}
        </div>
      ) : !health || !health.services || health.services.length === 0 ? (
        <div className="bg-card rounded-lg shadow-sm border border-border p-8 text-center">
          <p className="text-muted-foreground">No service health data available</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {health.services.map((service) => (
            <ServiceCard
              key={service.name}
              service={service}
              lastChecked={health.timestamp}
            />
          ))}
        </div>
      )}
    </div>
  );
}
